import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ReactNode, useMemo } from "react";
import { toast } from "react-toastify";
import { getAppealThreads, openStepAppeal, replyToAppeal } from "../api";
import { AppealsContext, AppealsContextValue } from "../context/appeals.context";
import { StepDetail } from "../dtos";
import {
  AppealAnchor,
  appealableItems,
  coveredByThread,
  sameItem,
  threadsForAnchor,
} from "../utils/appeals";

const appealThreadsKey = (stepUid: string): string[] => ["nawy-now", "appeals", stepUid];

/**
 * §Review-appeal: one read of the step's appeal threads, shared by every item that can carry one.
 *
 * The threads are fetched here (they are deliberately NOT part of the step envelope — an appeal renders
 * beside the item it challenges, never in the step conversation, and each thread carries `canReply` and
 * `isCurrentDecision`, both of which only the server can answer). What is appealable comes from the step
 * detail the card already holds, so nothing extra is read to know that.
 */
const AppealsProvider = ({
  stepUid,
  caseUid,
  detail,
  editable,
  children,
}: {
  stepUid: string;
  caseUid: string;
  detail: StepDetail;
  /** The agent can work this step — a settled or read-only step shows history without composers. */
  editable: boolean;
  children: ReactNode;
}) => {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: appealThreadsKey(stepUid),
    queryFn: () => getAppealThreads(stepUid),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: appealThreadsKey(stepUid) });
  };
  const open = useMutation({
    mutationFn: ({ anchor, content, attachmentUids }: { anchor: AppealAnchor; content: string; attachmentUids?: string[] }) =>
      openStepAppeal(stepUid, {
        kind: anchor.kind,
        fieldKey: anchor.fieldKey,
        uploadUid: anchor.uploadUid,
        content,
        attachmentUids,
      }),
    onSuccess: async () => {
      toast.success("Appeal sent to the reviewer");
      await refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Could not send the appeal"),
  });
  const reply = useMutation({
    mutationFn: ({ appealUid, content, attachmentUids }: { appealUid: string; content: string; attachmentUids?: string[] }) =>
      replyToAppeal(appealUid, content, attachmentUids),
    onSuccess: async () => {
      toast.success("Reply sent");
      await refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Could not send the reply"),
  });

  const threads = useMemo(() => data?.threads ?? [], [data]);
  const mayOpen = editable && (data?.mayOpen ?? false);
  const items = useMemo(() => appealableItems(detail), [detail]);
  const busy = open.isLoading || reply.isLoading;
  // mutateAsync, not mutate: the composers clear their draft only when the promise RESOLVES, so a refused
  // write (409 duplicate, closed window) keeps the agent's typed paragraph. onError above already toasts;
  // the component swallows the rejection.
  const openAppeal = open.mutateAsync;
  const replyOnAppeal = reply.mutateAsync;

  const value = useMemo<AppealsContextValue>(
    () => ({
      caseUid,
      stepUid,
      threadsFor: (anchor) => threadsForAnchor(threads, anchor),
      // Three independent gates, and the order matters only for cheapness: the caller's standing right
      // (server-resolved), then the Builder's opt-in plus a live decision to challenge (`items` is that
      // whole rule), then the round-trip rule — an item is blocked only while a thread covers its CURRENT
      // decision, so a replaced-and-rejected-again file becomes appealable a second time.
      canOpen: (anchor) =>
        mayOpen &&
        items.some((item) => sameItem(item, anchor)) &&
        !coveredByThread(threads, anchor),
      open: (anchor, content, attachmentUids) => openAppeal({ anchor, content, attachmentUids }),
      reply: (appealUid, content, attachmentUids) =>
        replyOnAppeal({ appealUid, content, attachmentUids }),
      busy,
    }),
    [caseUid, stepUid, threads, items, mayOpen, busy, openAppeal, replyOnAppeal]
  );

  return <AppealsContext.Provider value={value}>{children}</AppealsContext.Provider>;
};

export default AppealsProvider;
